import React from 'react';

import {assertAndContinue} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {SECTIONS} from 'constants/NavConstants';
import {NavStoreRegistry} from 'constants/RegistryConstants';
import NavStore from 'nav/NavStore';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {
  RegistryActivationFlowScreen,
  REGISTRY_ACTIVATION_MEMBERS,
  REGISTRY_ACTIVATION_ORG,
  REGISTRY_ACTIVATION_REGISTRY_EXISTS,
  REGISTRY_ACTIVATION_WEBINAR,
  REGISTRY_ACTIVATION_WELCOME,
} from 'onboarding/registry-activation-flow/RegistryActivationFlowConstants';

import MembersScreen from './MembersScreen';
import OrgScreen from './OrgScreen';
// @ts-expect-error
import * as RegistryActivationFlowActions from './RegistryActivationFlowActions';
import RegistryActivationFlowStore from './RegistryActivationFlowStore';
import WebinarScreen from './WebinarScreen';
// @ts-expect-error
import WelcomeScreen from './WelcomeScreen';

class RegistryActivationFlowViewConnector extends DataConnector<Props, EnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [NavStore, RegistryActivationFlowStore];

  enhanceProps(): EnhancedProps {
    return {
      registries: NavStore.getRegistries(),
      isLoading: this.registryActivationFlowStore().getIsLoading(),
    };
  }
}

interface Props {
  currentScreen: RegistryActivationFlowScreen;
}
interface EnhancedProps {
  registries: Array<NavStoreRegistry>;
  isLoading: boolean;
}

class RegistryActivationFlowView extends React.Component<Props & EnhancedProps> {
  getRegistry = (): NavStoreRegistry | null => this.props.registries?.[0] ?? null;

  getOrganizationNameFromRegistry = (): string => {
    const registry = this.getRegistry();
    return this.truncateOrganizationName(registry?.s_owner.name ?? '');
  };

  truncateOrganizationName = (name: string): string => {
    if (name.length > 25) {
      return `${name.substring(0, 24)}...`;
    }
    return name;
  };

  handleShowMembersScreen = (): void => {
    RegistryActivationFlowActions.showScreen(REGISTRY_ACTIVATION_MEMBERS);
  };

  handleShowWebinarScreen = (): void => {
    RegistryActivationFlowActions.showScreen(REGISTRY_ACTIVATION_WEBINAR);
  };

  handleStartUsingExistingRegistry = (): void => {
    RegistryActivationFlowActions.completeRegistryActivation(true);
    SidepanelActions.openSection(SECTIONS.REGISTRIES);
  };

  handleStartUsingRegistry = (): void => {
    RegistryActivationFlowActions.createRegistry(RegistryActivationFlowStore.getOrganizationId());
  };

  handleContinueCreatingRegistry = (): void => {
    if (this.getRegistry() == null) {
      RegistryActivationFlowActions.showScreen(REGISTRY_ACTIVATION_ORG);
    } else {
      RegistryActivationFlowActions.showScreen(REGISTRY_ACTIVATION_REGISTRY_EXISTS);
    }
  };

  render(): JSX.Element {
    return (
      <div className="registryActivation-container">
        {(() => {
          switch (this.props.currentScreen) {
            case REGISTRY_ACTIVATION_WELCOME:
              return (
                <WelcomeScreen
                  isLoading={!this.props.registries}
                  onContinue={this.handleContinueCreatingRegistry}
                />
              );
            case REGISTRY_ACTIVATION_ORG:
              return <OrgScreen onContinue={this.handleShowMembersScreen} isLoading={this.props.isLoading} />;
            case REGISTRY_ACTIVATION_MEMBERS:
              return <MembersScreen onContinue={this.handleShowWebinarScreen} />;
            case REGISTRY_ACTIVATION_REGISTRY_EXISTS:
              return (
                <WebinarScreen
                  headerText={`You have already been added to the ${this.getOrganizationNameFromRegistry()}
                  Registry!`}
                  paragraphText="Organizations are the perfect solution to managing data across your lab.
                    Inventory files in the Registry are all shared in one location, so team members can
                    easily collaborate."
                  existingRegistry={true}
                  onContinue={this.handleStartUsingExistingRegistry}
                />
              );
            case REGISTRY_ACTIVATION_WEBINAR:
              return (
                <WebinarScreen
                  headerText={`Congratulations, you finished setting up the
                    ${this.truncateOrganizationName(RegistryActivationFlowStore.getOrganizationName() ?? '')}
                    Registry!`}
                  paragraphText="You can edit more Registry settings like Schemas and Dropdowns by going to
                    the settings icon in your Registry sidepanel or expanded view."
                  existingRegistry={false}
                  onContinue={this.handleStartUsingRegistry}
                />
              );
            default:
              assertAndContinue(
                false,
                `RegistryActivationFlowView called with invalid screen: ${this.props.currentScreen}`
              );
              return null;
          }
        })()}
      </div>
    );
  }
}

export default RegistryActivationFlowViewConnector.connect(RegistryActivationFlowView);
